import {ChatMessageReactionSelection} from './chat-message-reaction-selection';
import {ReactionRestService} from '../../api/services/reaction.rest.service';
import {ReactionApiModel} from '../../api/models/reaction.api.model';
import {ReactionTypeEnum} from '../../core/enums/reaction-type.enum';
import {Injectable} from '@angular/core';
import {BehaviorSubject, defer, EMPTY, Observable, of, Subject, throwError} from "rxjs";
import {HttpContext, HttpHeaders} from "@angular/common/http";
import {catchError, expand, filter, finalize, map, scan, shareReplay, switchMap, takeUntil, tap, timeout} from "rxjs/operators";
import {plainToInstance} from 'class-transformer';
import {ChatCacheStoreService, chatConversationKey} from './chat-cache-store.service';
import {SKIP_CANCEL_ON_NAV} from '../../core/providers/http-context-tokens';
import {ConversationRestService} from "../../api/services/conversation.rest.service";
import {MessageRestService, CreateMessagePayload} from "../../api/services/message.rest.service";
import {ResultListApiModel} from "../../api/models/result-list.api.model";
import {ConversationApiModel} from "../../api/models/conversation.api.model";
import {ChatMessageApiModel} from "../../api/models/chat-message.api.model";
import {QueryParamsApiModel} from "../../api/models/query-params-api.model";
import {FilterParameterApiModel} from "../../api/models/filter-parameter.api.model";
import {EventSourceService} from "../../core/services/event-source.service";
import {EventSourceInterface} from "../../core/interfaces/event-source.interface";
import {chatMessageKey, matchesPendingMessage, mergeChatMessages, normalizeChatMessage} from './chat-message-state';
import {mergeConversationReadState} from './chat-conversation-state';

export interface ChatHistoryState {
  pageSize: number;
  lastPage: number;
  hasMore: boolean;
  total: number;
  /** Older snapshots and standalone previews have not proved a continuous range. */
  contiguous?: boolean;
}

export interface ChatSyncProgress<T> {
  pages: Array<{page: number; result: ResultListApiModel<T>}>;
  /** Only this emission proves that the requested range was reconciled. */
  complete: boolean;
}

interface ChatHeadState {signature: string; total: number; syncedAt: number; dirty?: boolean;}
export interface ChatThreadListState {scrollTop: number; filter: 'all' | 'listing' | 'direct'; page: number;}
export interface ChatMessageChange extends EventSourceInterface<ChatMessageApiModel> {clientTempId?: string;}

@Injectable({
  providedIn: 'root'
})
export class ChatDataProviderService {
  private listState: ChatThreadListState | null = null;
  get threadListState(): ChatThreadListState | null { return this.listState; }
  set threadListState(value: ChatThreadListState | null) { this.listState = value; this.persist('threads'); }
  private conversationsCache: ResultListApiModel<ConversationApiModel> | null = null;
  private messagesCache = new Map<string, ResultListApiModel<ChatMessageApiModel>>();
  private readonly historyStates = new Map<string, ChatHistoryState>();
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public readonly unreadCount$ = this.unreadCountSubject.asObservable();
  private unreadCountRequest$: Observable<number> | null = null;
  private readonly unreadCountReset = new Subject<void>();
  private unreadCountGeneration = 0;
  private unreadCountRevision = 0;
  private readonly unreadRequestCancel = new Subject<void>();
  private readonly readRequests = new Map<string, Observable<ConversationApiModel>>();
  private readonly heads = new Map<string, ChatHeadState>();
  private readonly revisions = new Map<string, number>();
  private readonly responseRevisions = new WeakMap<object, number>();
  private readonly conversationDetails = new Map<string, ConversationApiModel>();
  private initialized?: Promise<void>;
  private cancelRestore?: () => void;
  private storageReady = false;
  private readonly conversationEvents = new Subject<EventSourceInterface<ConversationApiModel>>();
  public readonly conversationEvents$ = this.conversationEvents.asObservable();
  private readonly messageChanges = new Subject<ChatMessageChange>();
  public readonly messageChanges$ = this.messageChanges.asObservable();
  private readonly recovery = new Subject<void>();
  public readonly recovery$ = this.recovery.asObservable();
  private readonly messageVersions = new WeakMap<ChatMessageApiModel, number>();
  private generation = 0;
  private readonly scheduledWrites = new Set<string>();
  private readonly dirtyWrites = new Set<string>();
  public readonly cacheReset$;
  public readonly conversationRevoked$;

  constructor(
    private conversationRS: ConversationRestService,
    private messageRS: MessageRestService,
    private eventSource: EventSourceService,
    private readonly disk: ChatCacheStoreService,
    private readonly reactionRS?: ReactionRestService,
  ) {
    this.cacheReset$ = disk.reset$;
    this.conversationRevoked$ = disk.conversationRevoked$;
    disk.conversationRevoked$.subscribe(iri => {
      this.clearMessagesCache(iri);
      this.conversationDetails.delete(iri);
      this.heads.delete(this.historyResource(iri));
      if (this.conversationsCache) this.conversationsCache.records = this.conversationsCache.records.filter(item => this.normalizeConversationKey(item['@id']) !== iri);
      this.persist('threads');
    });
    disk.reset$.subscribe(() => {
      this.generation++;
      this.readRequests.clear();
      this.cancelRestore?.();
      this.storageReady = false;
      this.initialized = undefined;
      this.conversationsCache = null;
      this.messagesCache.clear();
      this.historyStates.clear();
      this.heads.clear();
      this.revisions.clear();
      this.conversationDetails.clear();
      this.listState = null;
      this.scheduledWrites.clear();
      this.dirtyWrites.clear();
    });
  }

  public initialize(): Promise<void> {
    if (!this.disk.userKey) return Promise.resolve();
    if (this.initialized) return this.initialized;
    const generation = this.generation;
    let expired = false;
    let finish: () => void;
    const restored = new Promise<void>(resolve => {
      const timer = setTimeout(() => { expired = true; finish(); }, 1500);
      const cancel = () => { expired = true; finish(); };
      finish = () => {
        clearTimeout(timer);
        if (this.cancelRestore === cancel) this.cancelRestore = undefined;
        resolve();
      };
      this.cancelRestore = cancel;
    });
    this.initialized = restored;
    void Promise.resolve().then(() => this.disk.snapshots()).then(snapshots => {
      if (expired || generation !== this.generation) return;
      this.storageReady = true;
      for (const {resource, value} of snapshots) {
        // A network read committed during restoration is authoritative.
        if (this.heads.has(resource)) continue;
        if (!value || !Array.isArray(value.records) || !Number.isFinite(value.total)
          || !value.records.every(item => item && (typeof item['@id'] === 'string'
            || (resource.startsWith('history:') && !!item.id && !item.localStatus)))) continue;
        if (resource === 'threads') {
          const restored = plainToInstance(ConversationApiModel, value.records as object[]);
          const current = this.conversationsCache?.records || [];
          this.conversationsCache = new ResultListApiModel(ConversationApiModel);
          this.conversationsCache.records = this.mergeById(restored, current);
          this.conversationsCache.total = Math.max(value.total, this.conversationsCache.records.length);
          this.listState ??= value.listState || null;
        } else if (resource.startsWith('history:') && Number.isInteger(value.history?.lastPage) && value.history.lastPage > 0
          && Number.isInteger(value.history?.pageSize) && value.history.pageSize > 0) {
          const iri = resource.slice('history:'.length);
          const current = this.messagesCache.get(iri);
          const restored = plainToInstance(ChatMessageApiModel, value.records as object[]);
          const result = new ResultListApiModel<ChatMessageApiModel>(ChatMessageApiModel);
          result.records = mergeChatMessages(restored, current?.records || []);
          result.total = Math.max(value.total, current?.total || 0);
          this.messagesCache.set(iri, result);
          if (!this.historyStates.has(iri)) this.historyStates.set(iri, value.history);
          if (value.conversation?.['@id'] && !this.conversationDetails.has(iri)) this.conversationDetails.set(iri, plainToInstance(ConversationApiModel, value.conversation));
        } else continue;
        if (!this.heads.has(resource) && value.head?.syncedAt > 0) this.heads.set(resource, {...value.head, dirty: value.head.dirty || this.revisions.has(resource)});
      }
    }).catch(() => undefined).finally(() => finish());
    return restored;
  }

  public needsSync(resource: string): boolean {
    const head = this.heads.get(resource);
    return !head || !!head.dirty || Date.now() - head.syncedAt >= 60_000;
  }

  public needsMessagesSync(iri: string): boolean { return this.needsSync(this.historyResource(iri)); }
  public recordMessagesHead(iri: string, result: ResultListApiModel<ChatMessageApiModel>): void { this.recordHead(this.historyResource(iri), result); }

  public responseRevision(result: object): number { return this.responseRevisions.get(result) ?? -1; }
  public messagesRevision(iri: string): number { return this.revisions.get(this.historyResource(iri)) || 0; }

  /** Recovery never fetches closed conversations. The current view owns that read. */
  public recoverMessages(): void {
    for (const resource of new Set([...this.heads.keys(), ...Array.from(this.messagesCache.keys(), iri => this.historyResource(iri))])) {
      this.revisions.set(resource, (this.revisions.get(resource) || 0) + 1);
      const head = this.heads.get(resource);
      if (head) head.dirty = true;
      this.persist(resource);
    }
    this.recovery.next();
  }

  public invalidateHead(conversationIri?: string): void {
    const resources = ['threads', ...(conversationIri ? [this.historyResource(conversationIri)] : [])];
    for (const resource of resources) {
      this.revisions.set(resource, (this.revisions.get(resource) || 0) + 1);
      const head = this.heads.get(resource);
      if (head) head.dirty = true;
      this.persist(resource);
    }
  }

  public recordHead(resource: string, result: ResultListApiModel<any>): void {
    this.heads.set(resource, {signature: this.signature(result), total: result.total, syncedAt: Date.now(),
      dirty: this.responseRevisions.has(result) && this.responseRevisions.get(result) !== (this.revisions.get(resource) || 0)});
    this.persist(resource);
  }

  public synchronizeMessages(iri: string, lastPage: number, size: number, forceAll: boolean): Observable<ChatSyncProgress<ChatMessageApiModel>> {
    return defer(() => {
      const cached = (this.getMessagesCache(iri)?.records || []).filter(message => !message.localStatus);
      const boundary = cached[0];
      const boundaryKey = boundary && chatMessageKey(boundary);
      const resource = this.historyResource(iri);
      const head = this.heads.get(resource);
      const oldHead = head && {...head};
      if (head) { head.dirty = true; this.persist(resource); }
      let reachedBoundary = !boundary;
      const fetch = (page: number) => this.getMessages(iri, page, size, 'DESC', false, true).pipe(map(result => {
        const records = result.records || [];
        reachedBoundary ||= records.some(message => chatMessageKey(message) === boundaryKey)
          || (!!boundary?.createdAt && records.some(message => +new Date(message.createdAt) < +new Date(boundary.createdAt)));
        const unchanged = page === 1 && !forceAll && !oldHead?.dirty
          && this.getHistoryState(iri)?.contiguous === true && oldHead?.signature === this.signature(result);
        const hasMore = records.length > 0 && (result.view && Object.prototype.hasOwnProperty.call(result.view, 'hydra:next')
          ? !!result.view['hydra:next'] : page * size < result.total);
        return {page, result, complete: unchanged || !hasMore || (page >= Math.max(1, lastPage) && reachedBoundary)};
      }));
      return fetch(1).pipe(
        expand(page => page.complete ? EMPTY : fetch(page.page + 1), 1),
        scan((progress, page) => ({pages: [...progress.pages, {page: page.page, result: page.result}], complete: page.complete}),
          {pages: [], complete: false} as ChatSyncProgress<ChatMessageApiModel>),
      );
    });
  }

  public synchronizeThreads(lastPage: number, size: number, forceAll: boolean): Observable<ChatSyncProgress<ConversationApiModel>> {
    return defer(() => {
      const head = this.heads.get('threads');
      const oldHead = head && {...head};
      if (head) { head.dirty = true; this.persist('threads'); }
      const fetch = (page: number) => this.getConversations(page, size, false, true).pipe(map(result => ({
        page, result, complete: page >= Math.max(1, lastPage) || !result.records.length || page * size >= result.total
          || (page === 1 && !forceAll && !oldHead?.dirty && oldHead?.signature === this.signature(result)),
      })));
      return fetch(1).pipe(
        expand(page => page.complete ? EMPTY : fetch(page.page + 1), 1),
        scan((progress, page) => ({pages: [...progress.pages, {page: page.page, result: page.result}], complete: page.complete}),
          {pages: [], complete: false} as ChatSyncProgress<ConversationApiModel>),
      );
    });
  }

  public rememberConversation(conversation: ConversationApiModel): void {
    const iri = this.normalizeConversationKey(conversation?.['@id']);
    if (!iri) return;
    this.conversationDetails.set(iri, this.withKnownReadState(conversation));
    this.persist(this.historyResource(iri));
  }

  private withKnownReadState(conversation: ConversationApiModel): ConversationApiModel {
    const iri = this.normalizeConversationKey(conversation?.['@id']);
    const cached = this.conversationsCache?.records.find(item => this.normalizeConversationKey(item['@id']) === iri);
    const previous = mergeConversationReadState(this.conversationDetails.get(iri), cached);
    return {...conversation, ...mergeConversationReadState(previous, conversation),
      lastMessage: normalizeChatMessage(conversation.lastMessage)} as ConversationApiModel;
  }

  public forgetConversation(iri: string): void {
    const key = this.normalizeConversationKey(iri);
    void this.disk.forgetConversation(key);
  }

  private persist(resource: string): void {
    if (!this.disk.userKey) return;
    this.dirtyWrites.add(resource);
    if (this.scheduledWrites.has(resource)) return;
    this.scheduledWrites.add(resource);
    const generation = this.generation;
    void this.initialize().then(async () => {
      if (generation !== this.generation) return;
      // A timed-out store must not accumulate pending writes behind the blocked read.
      if (!this.storageReady) return;
      while (generation === this.generation && this.dirtyWrites.delete(resource)) {
        await this.persistSnapshot(resource);
      }
    }).catch(() => {}).finally(() => {
      if (generation !== this.generation) return;
      this.scheduledWrites.delete(resource);
      const repeat = this.storageReady && this.dirtyWrites.has(resource);
      this.dirtyWrites.delete(resource);
      if (repeat) this.persist(resource);
    });
  }

  private persistSnapshot(resource: string): Promise<void> {
    const head = this.heads.get(resource);
    if (resource === 'threads' && this.conversationsCache && head) {
      return this.disk.saveSnapshot(resource, {
        records: this.conversationsCache.records.map(item => this.confirmedConversation(item)), total: this.conversationsCache.total, listState: this.listState, head,
      }, head.syncedAt);
    }
    if (!resource.startsWith('history:')) return Promise.resolve();
    const iri = resource.slice('history:'.length);
    const history = this.historyStates.get(iri);
    const cached = this.messagesCache.get(iri);
    if (!head || !history?.lastPage || !cached) return Promise.resolve();
    return this.disk.saveSnapshot(resource, {
      records: cached.records.filter(item => !item.localStatus && !!item['@id']), total: cached.total,
      conversation: this.confirmedConversation(this.conversationDetails.get(iri)), history, head,
    }, head.syncedAt, iri);
  }

  private historyResource(iri: string): string { return `history:${this.normalizeConversationKey(iri)}`; }

  private confirmedConversation(conversation: ConversationApiModel): ConversationApiModel {
    if (!conversation?.lastMessage?.localStatus) return conversation;
    const records = this.messagesCache.get(this.normalizeConversationKey(conversation['@id']))?.records || [];
    const lastMessage = records.filter(item => !item.localStatus && item['@id']).slice(-1)[0] || null;
    return {...conversation, lastMessage, lastMessageAt: lastMessage?.createdAt || null} as ConversationApiModel;
  }

  private signature(result: ResultListApiModel<any>): string {
    return JSON.stringify([result.total, (result.records || []).map(item => [item['@id'] || item.id, item.updatedAt || item.createdAt, item.lastMessage?.['@id'], item.lastMessageAt])]);
  }

  private mergeById(existing: ConversationApiModel[], incoming: ConversationApiModel[]): ConversationApiModel[] {
    const records = new Map(existing.map(item => [this.normalizeConversationKey(item['@id']), item]));
    incoming.forEach(item => {
      const key = this.normalizeConversationKey(item['@id']);
      records.set(key, {...item, ...mergeConversationReadState(records.get(key), item)} as ConversationApiModel);
    });
    return Array.from(records.values());
  }

  public getConversations(
    page: number = 1,
    limit: number = 20,
    useCache: boolean = true,
    forceRefresh: boolean = false,
    softRefresh: boolean = false
  ): Observable<ResultListApiModel<ConversationApiModel>> {
    const params = new QueryParamsApiModel([], page, 'DESC', limit);
    if (page === 1 && useCache && this.conversationsCache) {
      return of(this.conversationsCache);
    }
    let headers: HttpHeaders | undefined;
    if (forceRefresh) {
      headers = new HttpHeaders({'x-cache-refresh': 'true'});
    } else if (softRefresh) {
      headers = new HttpHeaders({'x-cache-soft-refresh': 'true'});
    }
    const generation = this.generation;
    const revision = this.revisions.get('threads') || 0;
    return defer(() => this.conversationRS.getCollection(params, headers, new HttpContext().set(SKIP_CANCEL_ON_NAV, true))).pipe(
      timeout({first: 30_000}), takeUntil(this.disk.reset$), filter(() => generation === this.generation),
      map(result => { result.records = (result.records || []).map(item => this.withKnownReadState(item)); return result; }),
      tap(result => this.responseRevisions.set(result, revision)),
    );
  }

  public updateConversationsCache(result: ResultListApiModel<ConversationApiModel>): void {
    this.updateConversationsCacheFromRecords(result.records, result.total);
  }

  public updateConversationsCacheFromRecords(records: ConversationApiModel[], total: number): void {
    const known = new Map(this.conversationDetails);
    this.conversationsCache?.records.forEach(item => {
      const key = this.normalizeConversationKey(item['@id']);
      known.set(key, {...item, ...mergeConversationReadState(known.get(key), item)} as ConversationApiModel);
    });
    records = records.map(item => ({...item,
      ...mergeConversationReadState(known.get(this.normalizeConversationKey(item['@id'])), item),
    } as ConversationApiModel));
    if (this.conversationsCache) {
      this.conversationsCache.records = records;
      this.conversationsCache.total = total;
      this.persist('threads');
      return;
    }
    this.conversationsCache = new ResultListApiModel<ConversationApiModel>(ConversationApiModel);
    this.conversationsCache.records = records;
    this.conversationsCache.total = total;
    this.persist('threads');
  }

  public hasConversationsCache(): boolean {
    return !!this.conversationsCache?.records?.length;
  }

  public getConversationsCache(): ResultListApiModel<ConversationApiModel> | null {
    return this.conversationsCache;
  }

  public getConversationFromCacheById(id: string): ConversationApiModel | null {
    return this.conversationsCache?.records?.find((conversation) => {
      const iri = conversation?.['@id'];
      if (!iri) {
        return false;
      }
      if (iri.endsWith(`/conversations/${id}`) || iri.endsWith(`/api/conversations/${id}`)) {
        return true;
      }
      return iri.split('/').pop() === id;
    }) || this.conversationDetails.get(this.normalizeConversationKey(id)) || null;
  }

  public updateMessagesCacheFromRecords(
    conversationIri: string,
    records: ChatMessageApiModel[],
    total: number,
    history?: ChatHistoryState,
    readRevision?: number,
  ): void {
    const cacheKey = this.normalizeConversationKey(conversationIri);
    const existing = this.messagesCache.get(cacheKey);
    const priorRecords = existing?.records || [];
    // A component snapshot is not authoritative over confirmed realtime data.
    // Network commits can replace older rows, but never events newer than the read.
    const protectedRecords = priorRecords.filter(message => !message.localStatus && (readRevision === undefined
      ? this.messageVersions.has(message) : (this.messageVersions.get(message) || 0) > readRevision));
    const base = readRevision === undefined ? priorRecords.filter(message => !message.localStatus) : [];
    const result = existing || new ResultListApiModel<ChatMessageApiModel>(ChatMessageApiModel);
    const normalizedRecords = records.map(message => {
      const previous = priorRecords.find(item => chatMessageKey(item) === chatMessageKey(message));
      return previous ? mergeChatMessages([previous], [message])[0] : message;
    });
    result.records = mergeChatMessages(mergeChatMessages(base, normalizedRecords), protectedRecords);
    const committed = new Map(result.records.map(message => [chatMessageKey(message), message]));
    priorRecords.forEach(message => {
      const merged = committed.get(chatMessageKey(message));
      const version = this.messageVersions.get(message);
      if (merged && version !== undefined) this.messageVersions.set(merged, version);
    });
    result.total = Math.max(total, result.records.filter(message => !message.localStatus).length, protectedRecords.length ? existing?.total || 0 : 0,
      readRevision === undefined ? this.historyStates.get(cacheKey)?.total || 0 : 0);
    if (history) this.historyStates.set(cacheKey, {...history, total: result.total});
    this.messagesCache.set(cacheKey, result);
    this.persist(this.historyResource(cacheKey));
  }

  public clearMessagesCache(conversationIri: string): void {
    this.messagesCache.delete(this.normalizeConversationKey(conversationIri));
    this.historyStates.delete(this.normalizeConversationKey(conversationIri));
  }

  public getHistoryState(conversationIri: string): ChatHistoryState | undefined {
    return this.historyStates.get(this.normalizeConversationKey(conversationIri));
  }

  public hasMessagesCache(conversationIri: string): boolean {
    const cached = this.messagesCache.get(this.normalizeConversationKey(conversationIri));
    return !!cached?.records?.length;
  }

  public getMessagesCache(conversationIri: string): ResultListApiModel<ChatMessageApiModel> | null {
    return this.messagesCache.get(this.normalizeConversationKey(conversationIri)) || null;
  }

  public appendMessageToCache(conversationIri: string, message: ChatMessageApiModel): void {
    if (message && !message.localStatus && (message['@id'] || message.id)) {
      this.applyMessageEvent({payload: message, state: 'persisted', type: ''}, conversationIri);
      return;
    }
    this.upsertMessageToCache(conversationIri, message);
  }

  private upsertMessageToCache(conversationIri: string, message: ChatMessageApiModel): void {
    if (!message) {
      return;
    }
    const cacheKey = this.normalizeConversationKey(conversationIri || message.conversation);
    if (!cacheKey) {
      return;
    }
    const existing = this.messagesCache.get(cacheKey) || new ResultListApiModel<ChatMessageApiModel>(ChatMessageApiModel);
    existing.records = mergeChatMessages(existing.records || [], [message]);
    existing.total = Math.max(existing.total || 0, existing.records.filter(item => !item.localStatus).length);
    this.messagesCache.set(cacheKey, existing);
    this.persist(this.historyResource(cacheKey));
  }

  /** The only entry point for confirmed writes, irrespective of transport. */
  public applyMessageEvent(event: EventSourceInterface<ChatMessageApiModel>, conversationIri?: string, clientTempId?: string): void {
    const payload = event?.payload;
    const iri = this.normalizeConversationKey(conversationIri || payload?.conversation);
    if (!payload || !iri || (!payload['@id'] && !payload.id) || payload.localStatus) return;
    if (event.type === 'message-reactions') {
      this.applyReactionState({...payload, conversation: payload.conversation || iri} as ChatMessageApiModel);
      return;
    }
    const generation = this.generation;
    const message = normalizeChatMessage({...payload, conversation: payload.conversation || iri} as ChatMessageApiModel);
    const key = chatMessageKey(message);
    const cache = this.messagesCache.get(iri);
    const previous = cache?.records?.find(item => chatMessageKey(item) === key);
    message['@id'] = previous?.['@id'] || message['@id'];
    message.conversation = previous?.conversation || message.conversation;
    const previousTotal = cache?.total || 0;
    const latestConfirmed = (cache?.records || []).filter(item => !item.localStatus).slice(-1)[0];
    let reconciledTempId = clientTempId;
    if (event.state === 'removed') {
      if (!previous) return;
      cache.records = cache.records.filter(item => chatMessageKey(item) !== key);
      cache.total = Math.max(cache.records.length, cache.total - 1);
    } else {
      const merged = mergeChatMessages(previous ? [previous] : [], [message])[0];
      const candidates = (cache?.records || []).filter(item => item.localStatus && (clientTempId
        ? item.clientTempId === clientTempId : !previous && matchesPendingMessage(item, message)));
      const pending = candidates.length === 1 ? candidates[0] : undefined;
      reconciledTempId ||= pending?.clientTempId;
      if (pending) cache.records = cache.records.filter(item => item !== pending);
      if (previous && JSON.stringify(previous) === JSON.stringify(merged)) {
        if (pending) {
          this.persist(this.historyResource(iri));
          if (generation === this.generation) this.messageChanges.next({...event, payload: previous, clientTempId: reconciledTempId});
        }
        return;
      }
      this.upsertMessageToCache(iri, merged);
      if (generation !== this.generation) return;
      const committed = this.messagesCache.get(iri);
      if (!previous && latestConfirmed && +new Date(merged.createdAt) >= +new Date(latestConfirmed.createdAt)) {
        committed.total = Math.max(committed.total, previousTotal + 1);
      }
    }
    this.invalidateHead(iri);
    if (generation !== this.generation) return;
    const history = this.historyStates.get(iri);
    if (history) this.historyStates.set(iri, {...history, total: this.messagesCache.get(iri)?.total || 0, contiguous: false});
    const committed = this.messagesCache.get(iri)?.records.find(item => chatMessageKey(item) === key);
    if (committed) this.messageVersions.set(committed, this.revisions.get(this.historyResource(iri)) || 0);
    if (event.state !== 'removed') this.updateConversationPreview(iri, committed || message);
    this.persist(this.historyResource(iri));
    this.messageChanges.next({...event, payload: committed || message, clientTempId: reconciledTempId});
  }

  public setMessageReaction(message: ChatMessageApiModel, selection: ChatMessageReactionSelection): Observable<ChatMessageApiModel> {
    const generation = this.generation;
    return this.reactionRS.create(plainToInstance(ReactionApiModel, {
      subject: message['@id'], type: ReactionTypeEnum.MESSAGE_REACTION,
      ...(typeof selection === 'string' ? {reactedWith: selection} : selection),
    })).pipe(
      takeUntil(this.disk.reset$), filter(() => generation === this.generation),
      map(reaction => reaction.messageState),
      tap(snapshot => this.applyReactionState(snapshot)),
    );
  }

  public removeMessageReaction(message: ChatMessageApiModel, reaction: ReactionApiModel): Observable<ChatMessageApiModel> {
    const generation = this.generation;
    return this.reactionRS.delete(reaction).pipe(
      switchMap(() => this.messageRS.get(message['@id'])),
      takeUntil(this.disk.reset$), filter(() => generation === this.generation),
      tap(snapshot => this.applyReactionState(snapshot)),
    );
  }

  public applyReactionState(snapshot: ChatMessageApiModel): void {
    if (!snapshot || !Number.isInteger(snapshot.reactionsVersion) || !Array.isArray(snapshot.reactions)) return;
    const message = normalizeChatMessage(snapshot);
    const iri = this.normalizeConversationKey(message.conversation);
    if (!iri) return;
    const resource = this.historyResource(iri);
    const cache = this.messagesCache.get(iri);
    const key = chatMessageKey(message);
    const previous = cache?.records.find(item => chatMessageKey(item) === key);
    if (previous && (previous.reactionsVersion || 0) >= message.reactionsVersion) return;
    this.revisions.set(resource, (this.revisions.get(resource) || 0) + 1);
    const head = this.heads.get(resource);
    if (head) head.dirty = true;
    // An update to an unloaded message must not insert a new history row.
    if (previous) {
      const committed = {...previous, reactions: message.reactions, reactionsVersion: message.reactionsVersion};
      cache.records = cache.records.map(item => item === previous ? committed : item);
      this.messageVersions.set(committed, this.revisions.get(resource));
      this.messageChanges.next({payload: committed, state: 'updated', type: 'message-reactions'});
    }
    this.persist(resource);
  }

  public getConversation(conversationIri: string): Observable<ConversationApiModel> {
    const generation = this.generation;
    return defer(() => this.conversationRS.get(conversationIri)).pipe(timeout({first: 30_000}), takeUntil(this.disk.reset$), filter(() => generation === this.generation),
      map(conversation => this.withKnownReadState(conversation)),
      tap(conversation => this.rememberConversation(conversation)), catchError(error => generation === this.generation ? this.handleAccessError(error, conversationIri) : of()));
  }

  public getMessages(
    conversationIri: string,
    page: number = 1,
    limit: number = 30,
    order: 'ASC'|'DESC' = 'DESC',
    useCache: boolean = true,
    forceRefresh: boolean = false,
    softRefresh: boolean = false
  ): Observable<ResultListApiModel<ChatMessageApiModel>> {
    const conversationId = this.normalizeConversationId(conversationIri);
    const params = new QueryParamsApiModel([
      new FilterParameterApiModel('conversation.id', conversationId, 'single'),
      new FilterParameterApiModel('order[createdAt]', order, 'single')
    ], page, null, limit);

    if (page === 1 && useCache) {
      const cached = this.messagesCache.get(this.normalizeConversationKey(conversationIri));
      if (cached) {
        return of(cached);
      }
    }

    let headers: HttpHeaders | undefined;
    if (forceRefresh) {
      headers = new HttpHeaders({'x-cache-refresh': 'true'});
    } else if (softRefresh) {
      headers = new HttpHeaders({'x-cache-soft-refresh': 'true'});
    }
    // A response is a single DESC page, while the view cache is a merged ASC history.
    // Only the conversation owner may replace that cache after accepting the response.
    // Selecting a conversation starts before navigation. Its component owns
    // cancellation by conversation/generation, rather than by route changes.
    const generation = this.generation;
    const revision = this.revisions.get(this.historyResource(conversationIri)) || 0;
    return defer(() => this.messageRS.getCollection(params, headers, new HttpContext().set(SKIP_CANCEL_ON_NAV, true))).pipe(
      timeout({first: 30_000}),
      map(result => { result.records = (result.records || []).map(normalizeChatMessage); return result; }),
      takeUntil(this.disk.reset$), filter(() => generation === this.generation), tap(result => this.responseRevisions.set(result, revision)),
      catchError(error => generation === this.generation ? this.handleAccessError(error, conversationIri) : of()),
    );
  }

  private handleAccessError(error: any, iri: string): Observable<never> {
    if (error?.status === 403 || error?.status === 404) this.forgetConversation(iri);
    return throwError(() => error);
  }

  private normalizeConversationId(conversationIri: string): string {
    if (!conversationIri) {
      return '';
    }
    if (conversationIri.includes('/')) {
      return conversationIri.split('/').filter(Boolean).pop() || conversationIri;
    }
    return conversationIri;
  }

  public shouldUseMessagesCache(conversationIri: string, lastMessageAt?: Date | string): boolean {
    if (!lastMessageAt) {
      return true;
    }
    const cached = this.messagesCache.get(this.normalizeConversationKey(conversationIri));
    if (!cached?.records?.length) {
      return false;
    }
    const lastCached = cached.records[cached.records.length - 1];
    if (!lastCached?.createdAt) {
      return false;
    }
    return new Date(lastCached.createdAt) >= new Date(lastMessageAt);
  }

  private normalizeConversationKey(conversationIri: string): string {
    return chatConversationKey(conversationIri);
  }

  public createMessage(payload: CreateMessagePayload, clientTempId?: string): Observable<ChatMessageApiModel> {
    const generation = this.generation;
    return this.messageRS.create(payload).pipe(takeUntil(this.disk.reset$), filter(() => generation === this.generation),
      tap(message => this.applyMessageEvent({payload: message, state: 'persisted', type: ''}, undefined, clientTempId)));
  }

  public markRead(conversationId: string, lastMessage?: string): Observable<ConversationApiModel> {
    const generation = this.generation;
    const session = this.unreadCountGeneration;
    const key = this.normalizeConversationKey(conversationId);
    return defer(() => {
      if (!key || generation !== this.generation || session !== this.unreadCountGeneration) return EMPTY;
      const pending = this.readRequests.get(key);
      if (pending) return pending;
      const request$: Observable<ConversationApiModel> = defer(() => this.conversationRS.markRead(conversationId, lastMessage)).pipe(
        takeUntil(this.disk.reset$), takeUntil(this.unreadCountReset),
        takeUntil(this.conversationRevoked$.pipe(filter((iri: string) => this.normalizeConversationKey(iri) === key))),
        filter(conversation => generation === this.generation && session === this.unreadCountGeneration && !!conversation?.['@id']),
        tap(conversation => {
          this.applyConversationEvent({state: 'updated', type: '', payload: conversation});
          if (generation === this.generation && session === this.unreadCountGeneration) {
            this.getUnreadCount(true).subscribe({error: () => {}});
          }
        }),
        finalize(() => { if (this.readRequests.get(key) === request$) this.readRequests.delete(key); }),
        // Once sent, the mutation and cache update survive destruction of the view.
        shareReplay({bufferSize: 1, refCount: false}),
      );
      this.readRequests.set(key, request$);
      return request$;
    });
  }

  public hasPendingRead(conversationId: string): boolean {
    return this.readRequests.has(this.normalizeConversationKey(conversationId));
  }

  public touchPresence(conversationId: string): Observable<void> {
    return this.conversationRS.touchPresence(conversationId);
  }

  public clearPresence(): Observable<void> {
    return this.conversationRS.clearPresence();
  }

  public listenConversationMessages(conversationId: string): Observable<EventSourceInterface<ChatMessageApiModel>> {
    const generation = this.generation;
    return this.eventSource.listenSingleTopic<ChatMessageApiModel>(`/conversations/${conversationId}/messages`).pipe(
      takeUntil(this.disk.reset$), filter(() => generation === this.generation),
      tap(event => this.applyMessageEvent(event, conversationId)),
    );
  }

  public listenConversationList(userId: string, closable: boolean = true): Observable<EventSourceInterface<ConversationApiModel>> {
    return this.eventSource.listenSingleTopic<ConversationApiModel>(`/users/${userId}/conversations`, closable);
  }

  public applyConversationEvent(event: EventSourceInterface<ConversationApiModel>): void {
    const conversation = event?.payload;
    if (!conversation?.['@id']) return;
    const generation = this.generation;
    const key = this.normalizeConversationKey(conversation['@id']);
    const records = this.conversationsCache?.records || [];
    const previous = records.find(item => this.normalizeConversationKey(item['@id']) === key) || this.conversationDetails.get(key);
    const knownRead = mergeConversationReadState(this.conversationDetails.get(key), previous);
    const next = {...previous, ...conversation, ...mergeConversationReadState(knownRead, conversation),
      lastMessage: normalizeChatMessage(conversation.lastMessage || previous?.lastMessage),
      '@id': previous?.['@id'] || conversation['@id']} as ConversationApiModel;
    const timestamp = (item: ConversationApiModel) => new Date(item?.lastMessage?.createdAt || item?.lastMessageAt || 0).getTime() || 0;
    if (previous && timestamp(previous) > timestamp(next)) {
      next.lastMessage = previous.lastMessage;
      next.lastMessageAt = previous.lastMessageAt;
    }
    if (event.state !== 'removed' && next.lastMessage) this.applyMessageEvent({payload: next.lastMessage, state: 'persisted', type: event.type}, next['@id']);
    if (generation !== this.generation) return;
    this.rememberConversation(next);
    // Receipt/read metadata changes the thread row, not the message collection.
    this.invalidateHead();
    if (generation !== this.generation) return;
    const updated = [next, ...records.filter(item => this.normalizeConversationKey(item['@id']) !== key)]
      .sort((left, right) => timestamp(right) - timestamp(left));
    this.updateConversationsCacheFromRecords(updated, Math.max(this.conversationsCache?.total || 0, updated.length));
    this.conversationEvents.next({...event, payload: next});
  }

  public listenUnreadCount(userId: string, closable: boolean = true): Observable<EventSourceInterface<{count: number}>> {
    return this.eventSource.listenSingleTopic<{count: number}>(`/users/${userId}/unread-count`, closable);
  }

  public getUnreadCount(forceRefresh = false): Observable<number> {
    if (forceRefresh && this.unreadCountRequest$) {
      this.unreadRequestCancel.next();
      this.unreadCountRequest$ = null;
    }
    if (this.unreadCountRequest$) {
      return this.unreadCountRequest$;
    }

    const generation = this.unreadCountGeneration;
    const request$: Observable<number> = defer(() => {
      if (generation !== this.unreadCountGeneration) return EMPTY;
      const revision = this.unreadCountRevision;
      return this.conversationRS.getUnreadCount().pipe(filter(() => revision === this.unreadCountRevision));
    }).pipe(
      takeUntil(this.unreadCountReset), takeUntil(this.unreadRequestCancel),
      tap((count) => this.unreadCountSubject.next(count)),
      finalize(() => {
        if (this.unreadCountRequest$ === request$) this.unreadCountRequest$ = null;
      }),
      shareReplay(1)
    );

    this.unreadCountRequest$ = request$;
    return request$;
  }

  public resetUnreadCount(): void {
    this.unreadCountGeneration++;
    this.unreadCountRevision++;
    this.unreadCountReset.next();
    this.readRequests.clear();
    this.unreadCountRequest$ = null;
    this.unreadCountSubject.next(0);
  }

  public setUnreadCount(count: number): void {
    if (typeof count !== 'number') {
      return;
    }
    this.unreadCountRevision++;
    this.unreadCountSubject.next(count);
  }

  public updateConversationPreview(conversationIri: string, message: ChatMessageApiModel): void {
    if (!conversationIri || !message || !this.conversationsCache?.records?.length) {
      return;
    }
    const index = this.conversationsCache.records.findIndex((conversation) => this.normalizeConversationKey(conversation?.['@id']) === this.normalizeConversationKey(conversationIri));
    if (index < 0) {
      return;
    }
    const existing = this.conversationsCache.records[index];
    if (+new Date(existing.lastMessageAt || existing.lastMessage?.createdAt || 0) > +new Date(message.createdAt || 0)) return;
    const next = {
      ...existing,
      lastMessage: message,
      lastMessageAt: message.createdAt || new Date()
    } as ConversationApiModel;
    this.conversationsCache.records.splice(index, 1);
    this.conversationsCache.records.unshift(next);
    this.persist('threads');
  }

}
